import { checkout as checkoutApi, greetingCards } from '@consumer/api'
import { shippingOptions, type ShippingOption } from '@consumer/logic/shipping'
import type CreditCard from '@consumer/types/CreditCard'
import type DateStr from '@consumer/types/DateStr'
import type { GreetingCard, GreetingCardCategory } from '@consumer/types/serializers'
import { parseDate } from '@consumer/utils/date'

export interface GiftFeeScheduleItem {
  min: number
  max: number | null
  fee: number
}

export interface ShippingDates {
  firstclass: DateStr
  priority: DateStr
}

export interface CheckoutConfig {
  loaded?: boolean
  savedCard?: CreditCard
  christmasDeliveryDateCutoffs?: ShippingDates
  shipping: {
    baseFee: number
    options: ShippingOption[]
  }
  giftFeeSchedule: GiftFeeScheduleItem[]
  greetingCards: {
    categories: GreetingCardCategory[]
    defaultCard?: GreetingCard
  }
  configureShippingEstimates: (estimates: ShippingDates) => void
  load: (dynamicGreetingCardCategories?: boolean) => void
}

interface CheckoutConfigurationResponse {
  christmasDeliveryDateCutoffs?: ShippingDates
  shippingEstimates: ShippingDates
  giftFeeSchedule: GiftFeeScheduleItem[]
  savedCard: CreditCard
}

const checkoutConfig: CheckoutConfig = reactive({
  shipping: {
    baseFee: 5,
    options: shippingOptions,
  },
  giftFeeSchedule: [],
  greetingCards: {
    categories: [],
  },
  configureShippingEstimates: ({ firstclass: standard, priority }) => {
    const firstclassShippingEstimate = parseDate(standard, 'yyyy-MM-dd')
    const priorityShippingEstimate = parseDate(priority, 'yyyy-MM-dd')

    checkoutConfig.shipping.options[0].estimatedDeliveryDate = firstclassShippingEstimate
    checkoutConfig.shipping.options[1].estimatedDeliveryDate = firstclassShippingEstimate
    checkoutConfig.shipping.options[2].estimatedDeliveryDate = priorityShippingEstimate
  },
  load: () => {
    if (checkoutConfig.loaded) return

    // The default greeting card is only used by the skip-picker A/B test, so a
    // failure to fetch it must not block the rest of the checkout config.
    greetingCards.default<GreetingCard>()
      .then((defaultGreetingCard) => {
        checkoutConfig.greetingCards.defaultCard = defaultGreetingCard
      })
      .catch((error) => {
        console.error('Failed to load default greeting card:', error)
      })

    Promise.all([
      checkoutApi.configuration<CheckoutConfigurationResponse>(),
      greetingCards.categories<GreetingCardCategory[]>(),
    ]).then((values) => {
      const [configData, greetingCardCategories] = values
      checkoutConfig.savedCard = configData.savedCard
      checkoutConfig.giftFeeSchedule = configData.giftFeeSchedule
      checkoutConfig.configureShippingEstimates(configData.shippingEstimates)

      if (configData.christmasDeliveryDateCutoffs) {
        checkoutConfig.christmasDeliveryDateCutoffs = {
          firstclass: parseDate(configData.christmasDeliveryDateCutoffs?.firstclass, 'yyyy-MM-dd'),
          priority: parseDate(configData.christmasDeliveryDateCutoffs?.priority, 'yyyy-MM-dd'),
        }
      }
      checkoutConfig.greetingCards.categories = greetingCardCategories

      checkoutConfig.loaded = true
    }).catch((error) => {
      console.error('Failed to load checkout configuration:', error)
      // Make sure loading checkout config doesn't block rendering the payment form
      checkoutConfig.loaded = true
    })
  },
})

export default checkoutConfig
